import { A11yModule } from '@angular/cdk/a11y';
import { ChangeDetectionStrategy, Component, HostListener, ViewEncapsulation, input, output } from '@angular/core';

/** Visual appearance of the dialog. */
export type DialogAppearance = 'default' | 'compact';

/**
 * MstDialogComponent — accessible dialog shell.
 *
 * Full-page on mobile, lightbox on desktop.
 * Focus is trapped within the panel; Escape emits `closed`.
 * Content is fully consumer-defined.
 *
 * Use standalone in templates or programmatically via {@link MstDialogService}.
 *
 * @example template usage
 * @if (isOpen) {
 *   <mst-dialog (closed)="isOpen = false">
 *     <header class="mst-dialog__header">...</header>
 *     <div class="mst-dialog__content">...</div>
 *     <footer class="mst-dialog__footer">...</footer>
 *   </mst-dialog>
 * }
 */
@Component({
    selector: 'mst-dialog',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './dialog.component.html',
    styleUrl: './dialog.component.scss',
    encapsulation: ViewEncapsulation.None,
    imports: [A11yModule],
    host: {
        role: 'dialog',
        'aria-modal': 'true',
        class: 'mst-dialog',
        '[class.-compact]': 'appearance() === "compact"',
    },
})
export class MstDialogComponent {
    /** Whether clicking the backdrop closes the dialog. Default: true. */
    readonly closeOnBackdrop = input(true);

    /**
     * Visual appearance.
     * - `default`: full-page on mobile, lightbox on desktop. Close button top-right.
     * - `compact`: rounded card (16px), warm background, centered text. No close button.
     */
    readonly appearance = input<DialogAppearance>('default');

    /** Emitted when the close button (×) or Escape key is pressed. */
    readonly closed = output<void>();

    @HostListener('document:keydown.escape')
    protected onEscape(): void {
        this.closed.emit();
    }
}
